import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, PersonStanding, RotateCcw } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { UnitToggle } from "@/components/calculator/UnitToggle";
import { CopyResultButton } from "@/components/calculator/CopyResultButton";
import { ShareButton } from "@/components/calculator/ShareButton";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  BF_RANGES,
  categorize,
  deurenbergBodyFat,
  jp3BodyFat,
  massBreakdown,
  navyBodyFat,
  southAsianAdjusted,
  validateBMIMethod,
  validateJP3,
  validateNavy,
  weightToTarget,
  type BFCategoryKey,
  type BFMethod,
  type Gender,
} from "@/lib/calculators/bodyFat";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Body Fat Calculator", bn: "বডি ফ্যাট ক্যালকুলেটর" },
  desc: {
    en: "Estimate your body fat percentage with US Navy, BMI-based, or skin-caliper methods. Includes South Asian context for Bangladesh.",
    bn: "US Navy, BMI বা ক্যালিপার পদ্ধতিতে শরীরের চর্বির শতকরা হার নির্ণয় করুন। বাংলাদেশের জন্য দক্ষিণ এশীয় প্রসঙ্গসহ।",
  },
  method: { en: "Method", bn: "পদ্ধতি" },
  navy: { en: "US Navy (tape)", bn: "US Navy (টেপ)" },
  bmiMethod: { en: "BMI-based", bn: "BMI ভিত্তিক" },
  jp3: { en: "Skin caliper (3-site)", bn: "ক্যালিপার (৩-পয়েন্ট)" },

  gender: { en: "Gender", bn: "লিঙ্গ" },
  male: { en: "Male", bn: "পুরুষ" },
  female: { en: "Female", bn: "মহিলা" },
  age: { en: "Age", bn: "বয়স" },
  ageUnit: { en: "years", bn: "বছর" },
  height: { en: "Height (cm)", bn: "উচ্চতা (সেমি)" },
  weight: { en: "Weight (kg)", bn: "ওজন (কেজি)" },
  waist: { en: "Waist at navel (cm)", bn: "নাভির স্তরে কোমর (সেমি)" },
  neck: { en: "Neck below larynx (cm)", bn: "ঘাড় (গলার নিচে) (সেমি)" },
  hip: { en: "Hip at widest point (cm)", bn: "নিতম্ব (চওড়া অংশে) (সেমি)" },

  chest: { en: "Chest skinfold (mm)", bn: "বুকের চামড়াভাঁজ (মিমি)" },
  abdomen: { en: "Abdomen skinfold (mm)", bn: "পেটের চামড়াভাঁজ (মিমি)" },
  thigh: { en: "Thigh skinfold (mm)", bn: "উরুর চামড়াভাঁজ (মিমি)" },
  triceps: { en: "Triceps skinfold (mm)", bn: "ট্রাইসেপ চামড়াভাঁজ (মিমি)" },
  suprailiac: { en: "Suprailiac skinfold (mm)", bn: "সুপ্রাইলিয়াক চামড়াভাঁজ (মিমি)" },

  reset: { en: "Reset", bn: "রিসেট" },
  yourBf: { en: "Body Fat", bn: "শরীরের চর্বি" },
  category: { en: "Category", bn: "ক্যাটাগরি" },
  saContext: {
    en: "South Asian context: Bangladeshis tend to carry higher body fat at the same BMI than Caucasians, with greater cardiometabolic risk.",
    bn: "দক্ষিণ এশীয় প্রসঙ্গ: একই বিএমআইতে বাংলাদেশীদের শরীরের চর্বি সাধারণত বেশি থাকে এবং হৃদরোগ-ডায়াবেটিসের ঝুঁকি বেশি।",
  },
  saAdjusted: { en: "South Asian adjusted", bn: "দক্ষিণ এশীয় সমন্বিত" },

  totalWeight: { en: "Total weight", bn: "মোট ওজন" },
  fatMass: { en: "Fat mass", bn: "চর্বির ভর" },
  leanMass: { en: "Lean body mass", bn: "চর্বিহীন ভর" },
  leanNote: { en: "(muscles, organs, bones, water)", bn: "(পেশী, অঙ্গ, হাড়, পানি)" },

  goalTitle: { en: "Goal scenario", bn: "লক্ষ্য পরিকল্পনা" },
  reach: { en: "To reach", bn: "পৌঁছাতে" },
  loseFat: { en: "Lose fat", bn: "চর্বি কমান" },
  weeks: { en: "weeks at 0.5 kg/week", bn: "সপ্তাহ (০.৫ কেজি/সপ্তাহ)" },
  goCalorie: { en: "Plan it with the Calorie Calculator", bn: "ক্যালরি ক্যালকুলেটরে পরিকল্পনা করুন" },

  errorAcc: {
    en: "Body fat estimation has roughly ±3% error. For precision, consider a DEXA scan.",
    bn: "চর্বি নির্ণয়ে আনুমানিক ±৩% ত্রুটি থাকে। সঠিক মানের জন্য DEXA স্ক্যান করান।",
  },
  disclaimer: {
    en: "These methods are estimates — not a replacement for clinical measurement. Hydration, posture and tape technique affect results. Do not target unsafe body fat levels (men below 5%, women below 12%).",
    bn: "এই পদ্ধতিগুলো অনুমান — চিকিৎসা মাপ-পরিমাপের বিকল্প নয়। শরীরের পানি, ভঙ্গি ও টেপ-পদ্ধতি ফলাফলে প্রভাব ফেলে। অনিরাপদ চর্বি লক্ষ্য নেবেন না (পুরুষ ৫% এর নিচে, নারী ১২% এর নিচে)।",
  },

  err_height: { en: "Height seems incorrect.", bn: "উচ্চতা সঠিক মনে হচ্ছে না।" },
  err_weight: { en: "Weight seems incorrect.", bn: "ওজন সঠিক মনে হচ্ছে না।" },
  err_age: { en: "Age must be 15–100.", bn: "বয়স ১৫–১০০ এর মধ্যে।" },
  err_waist: { en: "Waist must be greater than neck.", bn: "কোমর ঘাড়ের চেয়ে বড় হতে হবে।" },
  err_neck: { en: "Neck measurement seems incorrect.", bn: "ঘাড়ের মাপ সঠিক মনে হচ্ছে না।" },
  err_hip: { en: "Hip measurement required for women.", bn: "নারীদের জন্য নিতম্বের মাপ দরকার।" },
  err_skin: { en: "Skinfold values must be 2–80 mm.", bn: "চামড়াভাঁজের মাপ ২–৮০ মিমি।" },
};

const CATEGORY_LABEL: Record<BFCategoryKey, Bilingual> = {
  essential: { en: "Essential fat", bn: "অপরিহার্য চর্বি" },
  athletes: { en: "Athletes", bn: "খেলোয়াড়" },
  fitness: { en: "Fitness", bn: "ফিটনেস" },
  average: { en: "Average / Healthy", bn: "গড় / স্বাস্থ্যকর" },
  obese: { en: "Obese", bn: "স্থূল" },
};

const CATEGORY_COLOR: Record<BFCategoryKey, string> = {
  essential: "bg-sky-400/30",
  athletes: "bg-emerald-500/30",
  fitness: "bg-emerald-400/30",
  average: "bg-yellow-400/30",
  obese: "bg-red-500/30",
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "What is a healthy body fat percentage?", bn: "স্বাস্থ্যকর শরীরের চর্বি শতকরা কত?" },
    a: {
      en: "For men, 14–24% is considered fitness-to-average; for women 21–31%. Athletes are lower. Levels below 5% (men) or 12% (women) are essential-fat territory and unsafe to target.",
      bn: "পুরুষদের জন্য ১৪–২৪% ফিটনেস থেকে গড়; নারীদের জন্য ২১–৩১%। খেলোয়াড়দের কম। পুরুষ ৫% বা নারী ১২% এর নিচে অপরিহার্য চর্বি — লক্ষ্য করা ঠিক নয়।",
    },
  },
  {
    q: { en: "How can I measure body fat at home?", bn: "ঘরে বসে চর্বি কীভাবে মাপা যায়?" },
    a: {
      en: "The most practical methods are the US Navy tape method (just a measuring tape) and BMI-based estimation. Skin calipers give better accuracy if you can do them correctly. BIA scales vary widely and are often unreliable.",
      bn: "সবচেয়ে সহজ পদ্ধতি US Navy টেপ পদ্ধতি (শুধু মাপের টেপ) এবং BMI-ভিত্তিক অনুমান। ক্যালিপার ভালো ফল দেয় যদি সঠিকভাবে নেওয়া হয়। BIA স্কেলগুলো প্রায়ই অনির্ভরযোগ্য।",
    },
  },
  {
    q: { en: "Body fat % vs BMI — which is better?", bn: "বডি ফ্যাট নাকি বিএমআই — কোনটি ভালো?" },
    a: {
      en: "Body fat % directly measures composition; BMI only uses height and weight. A muscular athlete can have a high BMI but low body fat. For long-term health, body fat % combined with waist circumference is more meaningful than BMI alone.",
      bn: "বডি ফ্যাট সরাসরি দেহের গঠন পরিমাপ করে; বিএমআই শুধু উচ্চতা-ওজন। পেশীবহুল খেলোয়াড়ের বিএমআই বেশি কিন্তু চর্বি কম হতে পারে। দীর্ঘমেয়াদী স্বাস্থ্যে বডি ফ্যাট ও কোমরের পরিধি একসঙ্গে বিএমআই থেকে বেশি অর্থবহ।",
    },
  },
  {
    q: { en: "Why are South Asian standards different?", bn: "দক্ষিণ এশীয় মান কেন আলাদা?" },
    a: {
      en: "Research (Deurenberg 2002 and others) shows South Asians have 3–5% higher body fat at the same BMI as Caucasians, with more visceral fat. This raises diabetes and cardiovascular risk at lower body weights.",
      bn: "গবেষণায় (Deurenberg ২০০২) দেখা গেছে একই বিএমআইতে দক্ষিণ এশীয়দের শরীরের চর্বি ৩–৫% বেশি, পেটের চর্বিও বেশি — যা কম ওজনেও ডায়াবেটিস ও হৃদরোগের ঝুঁকি বাড়ায়।",
    },
  },
  {
    q: { en: "How accurate is the US Navy method?", bn: "US Navy পদ্ধতি কতটা সঠিক?" },
    a: {
      en: "Around ±3% compared to DEXA when measurements are taken correctly. Errors come mainly from inconsistent tape placement.",
      bn: "সঠিকভাবে মাপলে DEXA এর তুলনায় প্রায় ±৩% ত্রুটি। ভুল মূলত টেপের অবস্থানের অসঙ্গতি থেকে আসে।",
    },
  },
  {
    q: { en: "What is lean body mass?", bn: "চর্বিহীন ভর কী?" },
    a: {
      en: "Lean body mass (LBM) is everything except fat — muscle, bone, organs, water. LBM = total weight − fat mass. Preserving LBM during weight loss is important; combine a moderate calorie deficit with strength training and sufficient protein.",
      bn: "চর্বিহীন ভর হলো শরীরের পেশী, হাড়, অঙ্গ ও পানি। LBM = মোট ওজন − চর্বির ওজন। ওজন কমানোর সময় LBM বজায় রাখতে শক্তি প্রশিক্ষণ ও পর্যাপ্ত প্রোটিন রাখুন।",
    },
  },
  {
    q: { en: "বডি ফ্যাট কীভাবে মাপা যায়?", bn: "বডি ফ্যাট কীভাবে মাপা যায়?" },
    a: {
      en: "ঘরে US Navy টেপ পদ্ধতি বা ক্যালিপার ব্যবহার করুন। হাসপাতালে DEXA, BodPod বা হাইড্রোস্ট্যাটিক ওয়েইং সবচেয়ে নির্ভুল।",
      bn: "ঘরে US Navy টেপ পদ্ধতি বা ক্যালিপার ব্যবহার করুন। হাসপাতালে DEXA, BodPod বা হাইড্রোস্ট্যাটিক ওয়েইং সবচেয়ে নির্ভুল।",
    },
  },
  {
    q: { en: "বাঙালিদের বডি ফ্যাট কত হওয়া উচিত?", bn: "বাঙালিদের বডি ফ্যাট কত হওয়া উচিত?" },
    a: {
      en: "দক্ষিণ এশীয় প্রসঙ্গে স্বাস্থ্যকর সীমা পুরুষদের জন্য আনুমানিক ১৫–২০% এবং নারীদের জন্য ২২–২৮%। এর বেশি হলে স্বাস্থ্যঝুঁকি বাড়ে।",
      bn: "দক্ষিণ এশীয় প্রসঙ্গে স্বাস্থ্যকর সীমা পুরুষদের জন্য আনুমানিক ১৫–২০% এবং নারীদের জন্য ২২–২৮%। এর বেশি হলে স্বাস্থ্যঝুঁকি বাড়ে।",
    },
  },
];

export const Route = createFileRoute("/body-fat-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Body Fat Calculator — US Navy & BMI Methods",
      description:
        "Free body fat percentage calculator. Multiple methods: US Navy tape, BMI-based, skin caliper. South Asian context for Bangladesh. বডি ফ্যাট ক্যালকুলেটর।",
      path: "/body-fat-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Body Fat Calculator", path: "/body-fat-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: BodyFatPage,
});

function BodyFatPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));
  const fmt1 = (n: number) => num(n.toFixed(1));

  const [method, setMethod] = useState<BFMethod>("navy");
  const [gender, setGender] = useState<Gender>("male");
  const [age, setAge] = useState("30");
  const [heightCm, setHeightCm] = useState("175");
  const [weightKg, setWeightKg] = useState("70");
  const [waistCm, setWaistCm] = useState("85");
  const [neckCm, setNeckCm] = useState("38");
  const [hipCm, setHipCm] = useState("95");

  // Skinfolds (mm)
  const [s1, setS1] = useState("12");
  const [s2, setS2] = useState("18");
  const [s3, setS3] = useState("14");

  const ageN = parseFloat(age);
  const hN = parseFloat(heightCm);
  const wN = parseFloat(weightKg);
  const waistN = parseFloat(waistCm);
  const neckN = parseFloat(neckCm);
  const hipN = parseFloat(hipCm);
  const s1N = parseFloat(s1);
  const s2N = parseFloat(s2);
  const s3N = parseFloat(s3);

  const result = useMemo(() => {
    if (method === "navy") {
      const v = validateNavy({
        gender, heightCm: hN, waistCm: waistN, neckCm: neckN,
        hipCm: gender === "female" ? hipN : undefined,
      });
      if (!v.ok) return { error: v.error as string };
      const bf = navyBodyFat({
        gender, heightCm: hN, waistCm: waistN, neckCm: neckN,
        hipCm: gender === "female" ? hipN : undefined,
      });
      return { bf, sa: southAsianAdjusted(bf, gender) };
    }
    if (method === "bmi") {
      const v = validateBMIMethod({ gender, age: ageN, heightCm: hN, weightKg: wN });
      if (!v.ok) return { error: v.error as string };
      const bf = deurenbergBodyFat({ gender, age: ageN, heightCm: hN, weightKg: wN });
      return { bf, sa: southAsianAdjusted(bf, gender) };
    }
    const v = validateJP3({ gender, age: ageN, s1: s1N, s2: s2N, s3: s3N });
    if (!v.ok) return { error: v.error as string };
    const bf = jp3BodyFat({ gender, age: ageN, s1: s1N, s2: s2N, s3: s3N });
    return { bf, sa: southAsianAdjusted(bf, gender) };
  }, [method, gender, ageN, hN, wN, waistN, neckN, hipN, s1N, s2N, s3N]);

  const errKey = "error" in result ? result.error : null;
  const bf = "bf" in result ? result.bf : null;
  const saBf = "sa" in result ? result.sa : null;

  const hasWeight = Number.isFinite(wN) && wN > 0;
  const mass = bf != null && hasWeight ? massBreakdown(wN, bf) : null;

  const cat = bf != null ? categorize(bf, gender) : null;

  // Goal scenario: target = upper bound of "fitness" range
  const targetPct = bf != null ? BF_RANGES[gender].fitness[1] : null;
  const goal = bf != null && targetPct != null && hasWeight && bf > targetPct
    ? {
        target: targetPct,
        loseKg: weightToTarget(wN, bf, targetPct),
      }
    : null;

  const reset = () => {
    setMethod("navy"); setGender("male"); setAge("30");
    setHeightCm("175"); setWeightKg("70");
    setWaistCm("85"); setNeckCm("38"); setHipCm("95");
    setS1("12"); setS2("18"); setS3("14");
  };

  const errorMsg = (() => {
    switch (errKey) {
      case "height_invalid": return tr("err_height");
      case "weight_invalid": return tr("err_weight");
      case "age_invalid": return tr("err_age");
      case "waist_invalid": return tr("err_waist");
      case "neck_invalid": return tr("err_neck");
      case "hip_invalid": return tr("err_hip");
      case "skinfold_invalid": return tr("err_skin");
      default: return null;
    }
  })();

  const shareLine = bf != null
    ? `${tr("yourBf")}: ${fmt1(bf)}% — ${CATEGORY_LABEL[cat!][lang]}`
    : "";

  return (
    <CalculatorLayout
      slug="body-fat-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="PersonStanding"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Method tabs */}
      <UnitToggle<BFMethod>
        label={tr("method")}
        value={method}
        onChange={setMethod}
        options={[
          { value: "navy", label: tr("navy") },
          { value: "bmi", label: tr("bmiMethod") },
          { value: "jp3", label: tr("jp3") },
        ]}
      />

      {/* Gender */}
      <div className="mt-5 space-y-1.5">
        <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("gender")}</label>
        <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
          {(["male", "female"] as Gender[]).map((g) => {
            const active = g === gender;
            return (
              <button
                key={g}
                type="button"
                onClick={() => setGender(g)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm font-medium transition",
                  active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                  lang === "bn" && "font-bn",
                )}
              >
                {g === "male" ? tr("male") : tr("female")}
              </button>
            );
          })}
        </div>
      </div>

      {/* Inputs per method */}
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        {method === "navy" && (
          <>
            <NumberInput label={tr("height")} value={heightCm} onChange={setHeightCm} unit="cm" />
            <NumberInput label={tr("waist")} value={waistCm} onChange={setWaistCm} unit="cm" />
            <NumberInput label={tr("neck")} value={neckCm} onChange={setNeckCm} unit="cm" />
            {gender === "female" && (
              <NumberInput label={tr("hip")} value={hipCm} onChange={setHipCm} unit="cm" />
            )}
            <NumberInput label={`${tr("weight")} (${lang === "bn" ? "ঐচ্ছিক" : "optional"})`} value={weightKg} onChange={setWeightKg} unit="kg" />
          </>
        )}

        {method === "bmi" && (
          <>
            <NumberInput label={tr("age")} value={age} onChange={setAge} unit={tr("ageUnit")} />
            <NumberInput label={tr("height")} value={heightCm} onChange={setHeightCm} unit="cm" />
            <NumberInput label={tr("weight")} value={weightKg} onChange={setWeightKg} unit="kg" />
          </>
        )}

        {method === "jp3" && (
          <>
            <NumberInput label={tr("age")} value={age} onChange={setAge} unit={tr("ageUnit")} />
            <NumberInput label={`${tr("weight")} (${lang === "bn" ? "ঐচ্ছিক" : "optional"})`} value={weightKg} onChange={setWeightKg} unit="kg" />
            <NumberInput label={gender === "male" ? tr("chest") : tr("triceps")} value={s1} onChange={setS1} unit="mm" />
            <NumberInput label={gender === "male" ? tr("abdomen") : tr("suprailiac")} value={s2} onChange={setS2} unit="mm" />
            <NumberInput label={tr("thigh")} value={s3} onChange={setS3} unit="mm" />
          </>
        )}
      </div>

      {errorMsg && (
        <p className={cn("mt-3 text-xs text-destructive", lang === "bn" && "font-bn")}>{errorMsg}</p>
      )}

      <div className="mt-5 flex justify-end">
        <Button variant="outline" onClick={reset} className="gap-2">
          <RotateCcw size={14} />
          {tr("reset")}
        </Button>
      </div>

      {/* Result */}
      {bf != null && cat && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-8 space-y-6"
        >
          <div className="rounded-xl border border-accent/30 bg-accent/5 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={cn("text-xs font-semibold uppercase tracking-wide text-accent", lang === "bn" && "font-bn")}>
                  {tr("yourBf")}
                </p>
                <p className="mt-1 flex items-baseline gap-2 text-4xl font-semibold tabular-nums text-foreground md:text-5xl">
                  <PersonStanding size={28} className="text-accent" />
                  {fmt1(bf)}%
                </p>
                <p className={cn("mt-2 text-sm font-medium", lang === "bn" && "font-bn")}>
                  {tr("category")}: <span className={cn("inline-block rounded px-2 py-0.5", CATEGORY_COLOR[cat])}>{CATEGORY_LABEL[cat][lang]}</span>
                </p>
                {saBf != null && method === "bmi" && (
                  <p className={cn("mt-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                    {tr("saAdjusted")}: <span className="font-semibold text-foreground tabular-nums">{fmt1(saBf)}%</span>
                  </p>
                )}
              </div>
              <CopyResultButton text={shareLine} />
            </div>

            {/* Gauge */}
            <BFGauge bf={bf} gender={gender} num={num} />
          </div>

          {/* SA context note */}
          <div className={cn(
            "flex items-start gap-3 rounded-xl border border-amber-400/30 bg-amber-400/5 p-4 text-xs text-foreground/80",
            lang === "bn" && "font-bn",
          )}>
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-500" />
            <p>{tr("saContext")}</p>
          </div>

          {/* Mass breakdown */}
          {mass && (
            <div className="grid gap-3 md:grid-cols-3">
              <Stat label={tr("totalWeight")} value={`${fmt1(wN)} kg`} lang={lang} />
              <Stat label={tr("fatMass")} value={`${fmt1(mass.fatKg)} kg`} lang={lang} />
              <Stat label={tr("leanMass")} value={`${fmt1(mass.leanKg)} kg`} lang={lang} sub={tr("leanNote")} />
            </div>
          )}

          {/* Goal */}
          {goal && (
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className={cn("mb-2 text-sm font-bold", lang === "bn" && "font-bn")}>{tr("goalTitle")}</h3>
              <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                {tr("reach")} <span className="font-semibold text-foreground">{num(goal.target)}%</span> ({CATEGORY_LABEL.fitness[lang]}):
              </p>
              <p className={cn("mt-1 text-base", lang === "bn" && "font-bn")}>
                {tr("loseFat")}: <span className="font-semibold tabular-nums">{fmt1(goal.loseKg)} kg</span>{" "}
                <span className="text-xs text-muted-foreground">≈ {num(Math.ceil(goal.loseKg / 0.5))} {tr("weeks")}</span>
              </p>
              <Link
                to="/calorie-calculator"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
              >
                <span className={lang === "bn" ? "font-bn" : ""}>{tr("goCalorie")}</span>
                <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {/* Accuracy */}
          <div className={cn(
            "flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-3 text-xs",
            lang === "bn" && "font-bn",
          )}>
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
            <p>{tr("errorAcc")}</p>
          </div>

          {/* Disclaimer */}
          <div className={cn(
            "flex items-start gap-3 rounded-xl border border-yellow-400/30 bg-yellow-400/5 p-4 text-xs text-foreground/80",
            lang === "bn" && "font-bn",
          )}>
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-yellow-500" />
            <p>{tr("disclaimer")}</p>
          </div>

          <div className="flex justify-end">
            <ShareButton title={shareLine} />
          </div>
        </motion.div>
      )}

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "bn" ? "বডি ফ্যাট শতকরা কী?" : "What is body fat percentage?"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "শরীরের মোট ওজনের যে অংশ চর্বি, সেটিই বডি ফ্যাট শতকরা। বাকি অংশ চর্বিহীন ভর — পেশী, হাড়, অঙ্গ ও পানি। বিএমআই থেকে এটি স্বাস্থ্যের আরও সঠিক ছবি দেয়।"
              : "Body fat percentage is the proportion of your total weight that is fat. The rest is lean body mass — muscle, bone, organs and water. It is a more meaningful picture of health than BMI alone."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "দক্ষিণ এশীয় ও বডি ফ্যাট" : "South Asians and body fat"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "Deurenberg (২০০২) ও পরবর্তী গবেষণায় দেখা গেছে — একই বিএমআইতে দক্ষিণ এশীয়দের শরীরের চর্বি ৩–৫% বেশি, পেটের চর্বিও বেশি। তাই বাংলাদেশীদের 'স্বাভাবিক বিএমআই' থাকলেও ডায়াবেটিস ও হৃদরোগের ঝুঁকি বেশি হতে পারে।"
              : "Deurenberg (2002) and follow-up studies show South Asians carry 3–5% more body fat at the same BMI, with greater visceral fat. As a result Bangladeshis can still have elevated diabetes and cardiovascular risk while sitting in a 'normal' BMI band."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "মাপার পদ্ধতি তুলনা" : "Measurement methods compared"} lang={lang}>
          <ul className={cn("space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li><span className="font-semibold text-foreground">DEXA / hydrostatic:</span> {lang === "bn" ? "সবচেয়ে নির্ভুল, হাসপাতাল প্রয়োজন।" : "Most accurate; requires lab/clinic."}</li>
            <li><span className="font-semibold text-foreground">Skin caliper:</span> {lang === "bn" ? "সঠিক হলে ±৩%, কিন্তু কৌশল-নির্ভর।" : "±3% if done correctly; technique-sensitive."}</li>
            <li><span className="font-semibold text-foreground">US Navy tape:</span> {lang === "bn" ? "ঘরে সহজে, ±৩% ত্রুটি।" : "Easy at home, ±3% error."}</li>
            <li><span className="font-semibold text-foreground">BMI-based (Deurenberg):</span> {lang === "bn" ? "শুধু অনুমান, পেশী হিসাবে নেয় না।" : "Estimate only; ignores muscle mass."}</li>
            <li><span className="font-semibold text-foreground">BIA scales:</span> {lang === "bn" ? "ফল পরিবর্তনশীল, পানির মাত্রায় প্রভাবিত।" : "Highly variable; affected by hydration."}</li>
          </ul>
        </Section>

        <Section title={lang === "bn" ? "স্বাস্থ্যকর সীমা" : "Healthy ranges"} lang={lang}>
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("category")}</th>
                  <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("male")}</th>
                  <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("female")}</th>
                </tr>
              </thead>
              <tbody>
                {(["essential", "athletes", "fitness", "average", "obese"] as BFCategoryKey[]).map((k) => (
                  <tr key={k} className="border-t border-border">
                    <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>
                      <span className={cn("inline-block rounded px-2 py-0.5", CATEGORY_COLOR[k])}>{CATEGORY_LABEL[k][lang]}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{rangeText(BF_RANGES.male[k], num)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{rangeText(BF_RANGES.female[k], num)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title={lang === "bn" ? "সঠিকভাবে মাপ নিন" : "How to measure correctly"} lang={lang}>
          <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "bn" ? "কোমর: নাভি বরাবর, পেট চেপে নয়।" : "Waist: at navel level, do not suck in your stomach."}</li>
            <li>{lang === "bn" ? "ঘাড়: গলার ঠিক নিচে সবচেয়ে সরু অংশে।" : "Neck: at the narrowest point just below the larynx."}</li>
            <li>{lang === "bn" ? "নিতম্ব (নারী): সবচেয়ে চওড়া অংশ বরাবর।" : "Hip (women): at the widest point of the buttocks."}</li>
            <li>{lang === "bn" ? "টেপ চামড়ায় সমতল রাখুন, কষবেন না।" : "Keep the tape flat against the skin, do not pull tight."}</li>
            <li>{lang === "bn" ? "সকালে, খালি পেটে মাপুন।" : "Measure in the morning, fasted."}</li>
          </ul>
        </Section>

        <Section title={lang === "bn" ? "সতর্কতা" : "Caveats"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "কোনো ঘরোয়া পদ্ধতি DEXA বা হাইড্রোস্ট্যাটিক ওয়েইং এর বিকল্প নয়। একই দিনে ভিন্ন সময়ে ফলাফল ভিন্ন হতে পারে। নিজের অগ্রগতি দেখতে একই পদ্ধতি ও একই সময় ব্যবহার করুন।"
              : "No home method replaces DEXA or hydrostatic weighing. Results vary across the day. To track progress, use the same method at the same time of day."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "দাবিত্যাগ" : "Disclaimer"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("disclaimer")}</p>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

function rangeText([a, b]: [number, number], num: (n: number | string) => string) {
  if (b >= 100) return `${num(a)}%+`;
  return `${num(a)}–${num(b)}%`;
}

function BFGauge({ bf, gender, num }: { bf: number; gender: Gender; num: (n: number | string) => string }) {
  const min = 0, max = 50;
  const pos = Math.max(0, Math.min(1, (bf - min) / (max - min)));
  const segs: { key: BFCategoryKey; from: number; to: number; color: string }[] = [];
  const r = BF_RANGES[gender];
  (["essential", "athletes", "fitness", "average", "obese"] as BFCategoryKey[]).forEach((k) => {
    const [a, b] = r[k];
    segs.push({ key: k, from: a, to: Math.min(b, max), color: CATEGORY_COLOR[k] });
  });
  return (
    <div className="mt-5">
      <div className="relative h-3 w-full overflow-hidden rounded-full bg-muted">
        <div className="absolute inset-0 flex">
          {segs.map((s) => {
            const width = ((s.to - s.from) / (max - min)) * 100;
            return <div key={s.key} className={cn("h-full", s.color)} style={{ width: `${width}%` }} />;
          })}
        </div>
        <motion.div
          initial={{ left: "0%" }}
          animate={{ left: `${pos * 100}%` }}
          transition={{ type: "spring", stiffness: 120, damping: 18 }}
          className="absolute -top-1 h-5 w-1 -translate-x-1/2 rounded-sm bg-foreground shadow"
        />
      </div>
      <div className="mt-1 flex justify-between text-[10px] tabular-nums text-muted-foreground">
        <span>{num(0)}%</span>
        <span>{num(r.fitness[1])}%</span>
        <span>{num(r.average[1])}%</span>
        <span>{num(50)}%</span>
      </div>
    </div>
  );
}

function Stat({ label, value, lang, sub }: { label: string; value: string; lang: "en" | "bn"; sub?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className={cn("text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{label}</p>
      <p className={cn("mt-1 text-lg font-semibold tabular-nums", lang === "bn" && "font-bn")}>{value}</p>
      {sub && <p className={cn("mt-0.5 text-[10px] text-muted-foreground", lang === "bn" && "font-bn")}>{sub}</p>}
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
